/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { NotFoundError, throwAsServiceError } from "@/lib/queryErrors";
import { getAttributionForInsert } from "@/lib/attribution";
import { estimateLeadValue } from "@/lib/analytics";
import { bridgeLeadToGhl, trackLeadConversion } from "@/lib/lead-tracking";

export interface PublicPackage {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  pricing_type: string;
  price: number;
  min_guests: number | null;
  max_guests: number | null;
  selection_mode: string;
  cover_image_url: string | null;
  og_image_url: string | null;
  cta_text: string;
  show_prices: boolean;
  items?: { id: string; menu_item_id: string; quantity: number; notes: string | null; menu_item?: { id: string; name: string; description: string | null; dietary_tags: string[]; category: { id: string; name: string } } }[];
  event_types?: { id: string; event_type_id: string; event_type?: { id: string; name: string } }[];
  category_slots?: { id: string; category_id: string; quantity: number; sort_order: number; label_override: string | null; category?: { id: string; name: string }; exclusions?: { menu_item_id: string }[] }[];
}

// Query config is factored out so route loaders can kick off the same
// fetch in parallel with component code loading (issue #147). The hook
// below reuses the exact same queryKey so it hydrates from the loader.
export const publicPackageQuery = (slug: string) => ({
  queryKey: ["public_package", slug] as const,
  queryFn: async (): Promise<PublicPackage> => {
    // `.maybeSingle()` lets us distinguish the "no rows" case (bad slug)
    // from the "something blew up" case (network / 5xx). See issue #144.
    const { data, error } = await (supabase as any)
      .from("packages")
      .select("*, items:package_items(*, menu_item:menu_items(id, name, description, dietary_tags, category:menu_categories(id, name))), event_types:package_event_types(*, event_type:event_types(id, name)), category_slots:package_category_slots(*, category:menu_categories(id, name), exclusions:package_slot_exclusions(menu_item_id))")
      .eq("slug", slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throwAsServiceError(error);
    if (!data) throw new NotFoundError(`Package "${slug}" not found`);
    return data as PublicPackage;
  },
});

export function usePublicPackage(slug: string) {
  return useQuery({
    ...publicPackageQuery(slug),
    // Don't retry a 404 — the slug doesn't exist, retrying won't help.
    retry: (failureCount, error) => {
      if (error instanceof NotFoundError) return false;
      return failureCount < 1;
    },
  });
}

export const publishedPackagesQuery = () => ({
  queryKey: ["published_packages"] as const,
  queryFn: async (): Promise<PublicPackage[]> => {
    const { data, error } = await (supabase as any)
      .from("packages")
      .select("*, event_types:package_event_types(*, event_type:event_types(id, name)), category_slots:package_category_slots(*, category:menu_categories(id, name))")
      .eq("is_published", true)
      .eq("is_active", true)
      .order("name");
    if (error) throwAsServiceError(error);
    return (data ?? []) as PublicPackage[];
  },
});

export function usePublishedPackages() {
  return useQuery(publishedPackagesQuery());
}

export function useSubmitPackageInquiry() {
  return useMutation({
    mutationFn: async (data: {
      package_id: string;
      name: string;
      email: string;
      phone?: string;
      event_date?: string;
      guest_count?: number;
      message?: string;
      idempotency_key?: string;
      /** The Lead's funnel tag (#925), the page slug. Tracking only: never inserted. */
      content_category?: string;
    }) => {
      // Generate the id client-side so the bridge invocation can reference it
      // without needing an anon SELECT RLS policy on menu_inquiries. Mirrors
      // useSubmitMenuInquiry — both hooks write to the same menu_inquiries
      // table (dual-role: menu_id OR package_id set).
      const id = crypto.randomUUID();
      const { content_category, ...row } = data;
      const { error } = await (supabase as any)
        .from("menu_inquiries")
        .insert({ id, ...row, ...getAttributionForInsert() });
      if (error) {
        // PostgreSQL unique violation — duplicate idempotency_key means
        // this submission was already recorded. Treat as success.
        if (error.code === "23505" && data.idempotency_key) return;
        throw error;
      }
      // GAP 1 fix: package inquiries now fire the GHL lead bridge just like
      // menu inquiries do (parity with #448 for /menu/:slug submissions).
      // Bridge is idempotent on menu_inquiries.ghl_synced_at and treats the
      // menu_id-null case gracefully (menu CTA falls back to plain copy in
      // resolveMenuForInquiry).
      bridgeLeadToGhl("menu_inquiry", id);
      // #738: same Lead gap as the menu path — package inquiries write the same
      // menu_inquiries row, so they take the same app-native Lead event.
      trackLeadConversion({
        rowId: id,
        source: "menu_inquiry",
        value: estimateLeadValue(data.guest_count),
        contentCategory: content_category,
      });
    },
    onSuccess: () => {
      toast.success("Inquiry submitted! We'll be in touch soon.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
